import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useLoadScript, Marker, Polyline } from '@react-google-maps/api';
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { extractDeviceGPS, normalizeSerial } from '../../utils/powerMapUtils';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];
const defaultCenter = { lat: 32.0853, lng: 34.7818 };

// SEGMENT_PALETTE — distinct colors cycled per segment. Mirrors the
// palette used by DeviceMapView's segment overlay for visual continuity.
export const SEGMENT_PALETTE = [
  '#2196F3', '#9C27B0', '#FF9800', '#00BCD4', '#E91E63',
  '#3F51B5', '#FF6F00', '#00ACC1', '#8E24AA', '#FFA726',
  '#43A047', '#D81B60', '#5E35B1', '#00897B', '#F4511E',
];

const UNASSIGNED_COLOR = '#9e9e9e';

export function segmentColorForIndex(idx) {
  return SEGMENT_PALETTE[idx % SEGMENT_PALETTE.length];
}

// buildSegmentColorMap assigns each segment key a stable palette color.
export function buildSegmentColorMap(segments) {
  const map = new Map();
  (segments || []).forEach((seg, idx) => {
    map.set(seg.key, segmentColorForIndex(idx));
  });
  return map;
}

const SegmentMapView = ({
  devices,
  segments,
  unassigned,
  selectedSegmentKey,
  onSelectSegment,
  height = 520,
}) => {
  const theme = useTheme();
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const gpsMap = useMemo(() => extractDeviceGPS(devices), [devices]);
  const colorByKey = useMemo(() => buildSegmentColorMap(segments), [segments]);

  // Per-device segment membership: serialKey -> { key, color, isCap }.
  const deviceSegment = useMemo(() => {
    const m = new Map();
    (segments || []).forEach((seg) => {
      const color = colorByKey.get(seg.key);
      (seg.cap_serials || []).forEach((cap) => {
        m.set(normalizeSerial(cap), { key: seg.key, color, isCap: true });
      });
      (seg.members || []).forEach((member) => {
        m.set(normalizeSerial(member.serial), { key: seg.key, color, isCap: false });
      });
    });
    return m;
  }, [segments, colorByKey]);

  const unassignedSet = useMemo(
    () => new Set((unassigned || []).map(normalizeSerial)),
    [unassigned],
  );

  // Markers: every device with GPS, colored by segment (grey if unassigned).
  const markers = useMemo(() => {
    const out = [];
    gpsMap.forEach((gps, serialKey) => {
      const seg = deviceSegment.get(serialKey);
      out.push({
        serial: gps.serial,
        serialKey,
        lat: gps.lat,
        lng: gps.lng,
        isCap: seg?.isCap ?? gps.isCap,
        connected: gps.connected,
        segmentKey: seg?.key || null,
        color: seg?.color || UNASSIGNED_COLOR,
        unassigned: unassignedSet.has(serialKey) || !seg,
      });
    });
    return out;
  }, [gpsMap, deviceSegment, unassignedSet]);

  // Links: each member -> its parent_in_segment, colored by segment.
  const links = useMemo(() => {
    const out = [];
    (segments || []).forEach((seg) => {
      const color = colorByKey.get(seg.key);
      (seg.members || []).forEach((member) => {
        const parent = member.parent_in_segment;
        if (!parent) return;
        const childKey = normalizeSerial(member.serial);
        const parentKey = normalizeSerial(parent);
        const childGps = gpsMap.get(childKey);
        const parentGps = gpsMap.get(parentKey);
        if (!childGps || !parentGps) return;
        out.push({
          id: `${seg.key}:${childKey}:${parentKey}`,
          segmentKey: seg.key,
          color,
          childLat: childGps.lat,
          childLng: childGps.lng,
          parentLat: parentGps.lat,
          parentLng: parentGps.lng,
        });
      });
    });
    return out;
  }, [segments, gpsMap, colorByKey]);

  const mapColorScheme = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.ColorScheme) return undefined;
    return theme.palette.mode === 'dark'
      ? window.google.maps.ColorScheme.DARK
      : window.google.maps.ColorScheme.LIGHT;
  }, [isLoaded, theme.palette.mode]);

  const fitToContent = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;
    markers.forEach((mk) => {
      bounds.extend({ lat: mk.lat, lng: mk.lng });
      hasPoints = true;
    });
    if (hasPoints) map.fitBounds(bounds, 48);
  }, [markers]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    fitToContent();
  }, [fitToContent]);

  useEffect(() => {
    fitToContent();
  }, [fitToContent]);

  const mapCenter = useMemo(() => {
    if (markers.length) {
      const latSum = markers.reduce((s, m) => s + m.lat, 0);
      const lngSum = markers.reduce((s, m) => s + m.lng, 0);
      return { lat: latSum / markers.length, lng: lngSum / markers.length };
    }
    return defaultCenter;
  }, [markers]);

  const isDimmed = useCallback(
    (segmentKey) => Boolean(selectedSegmentKey) && segmentKey !== selectedSegmentKey,
    [selectedSegmentKey],
  );

  if (loadError) {
    return <Alert severity="error">Failed to load Google Maps.</Alert>;
  }
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 10,
          left: 10,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 2,
          px: 1,
          py: 0.75,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          maxWidth: 'min(480px, calc(100% - 20px))',
          maxHeight: 120,
          overflowY: 'auto',
        }}
      >
        {(segments || []).map((seg) => (
          <Box
            key={seg.key}
            role="button"
            tabIndex={0}
            onClick={() => onSelectSegment?.(selectedSegmentKey === seg.key ? null : seg.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onSelectSegment?.(selectedSegmentKey === seg.key ? null : seg.key);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              opacity: isDimmed(seg.key) ? 0.4 : 1,
              fontWeight: selectedSegmentKey === seg.key ? 700 : 400,
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorByKey.get(seg.key), flexShrink: 0 }} />
            <Typography variant="caption" sx={{ lineHeight: 1.2, fontWeight: 'inherit' }}>
              {String(seg.cap_serials?.[0] || seg.key)} ({seg.member_count ?? seg.members?.length ?? 0})
            </Typography>
          </Box>
        ))}
        {(unassigned || []).length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: UNASSIGNED_COLOR, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
              Unassigned ({unassigned.length})
            </Typography>
          </Box>
        )}
      </Box>

      {!markers.length ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
            No devices with GPS data to display. Ensure devices have location data.
          </Typography>
        </Box>
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={12}
          onLoad={handleMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
            mapId: 'RRM_SEGMENT_MAP',
            colorScheme: mapColorScheme,
          }}
        >
          {links.map((link) => {
            const dim = isDimmed(link.segmentKey);
            return (
              <Polyline
                key={link.id}
                path={[
                  { lat: link.childLat, lng: link.childLng },
                  { lat: link.parentLat, lng: link.parentLng },
                ]}
                options={{
                  strokeColor: link.color,
                  strokeWeight: dim ? 1.5 : 3,
                  strokeOpacity: dim ? 0.25 : 0.8,
                  clickable: true,
                  icons: [{
                    icon: {
                      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                      scale: 2.5,
                      strokeColor: link.color,
                      fillColor: link.color,
                      fillOpacity: dim ? 0.25 : 1,
                      strokeWeight: 1,
                    },
                    offset: '85%',
                  }],
                }}
                onClick={() => onSelectSegment?.(selectedSegmentKey === link.segmentKey ? null : link.segmentKey)}
              />
            );
          })}

          {markers.map((mk) => {
            const dim = !mk.unassigned && isDimmed(mk.segmentKey);
            return (
              <Marker
                key={mk.serial}
                position={{ lat: mk.lat, lng: mk.lng }}
                title={mk.serial}
                onClick={() => mk.segmentKey && onSelectSegment?.(selectedSegmentKey === mk.segmentKey ? null : mk.segmentKey)}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: mk.isCap ? 8 : 5,
                  fillColor: mk.color,
                  fillOpacity: mk.unassigned ? 0.45 : (dim ? 0.35 : 0.9),
                  strokeColor: mk.isCap ? '#212121' : '#fff',
                  strokeWeight: mk.isCap ? 2 : 1,
                }}
              />
            );
          })}
        </GoogleMap>
      )}
    </Box>
  );
};

export default SegmentMapView;
